import { useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../api/client'
import type { SalesPersonDetailOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Kpi } from '../components/Kpi'
import { Pager } from '../components/Pager'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { useFilters, useParam, usePaging } from '../lib/filters'
import { parsePersonKey } from '../lib/salesPerson'
import {
  branchName,
  count,
  formatDate,
  money,
  moneyCompact,
  num,
  percent,
  shortDate,
} from '../lib/format'

const BUCKETS = ['auto', 'day', 'week', 'month'] as const
type Bucket = (typeof BUCKETS)[number]

export function SalesPersonDetailPage() {
  const { person = '' } = useParams()
  const { salesPersonId, name } = parsePersonKey(person)
  const { filters } = useFilters()
  const { offset, limit, setOffset } = usePaging(25)
  const [bucket, setBucket] = useParam<Bucket>('bucket', 'auto', BUCKETS)
  const [params] = useSearchParams()

  const query = useQuery({
    queryKey: [
      'salesperson',
      person,
      filters.from,
      filters.to,
      filters.outletId,
      filters.scope,
      bucket,
      offset,
      limit,
    ],
    queryFn: () =>
      api.get<SalesPersonDetailOut>('/analytics/salespeople/detail', {
        sales_person_id: salesPersonId,
        name,
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
        bucket,
        invoices_limit: limit,
        invoices_offset: offset,
      }),
  })

  const detail = query.data
  const stats = detail?.stats
  // Both services deploy from one push but never land at the same instant. A
  // bare .length on a field the older backend does not send yet would blank
  // the whole page during that window, so read the new ones defensively.
  const topProducts = detail?.top_products ?? []
  const topCustomers = detail?.top_customers ?? []
  const outlets = detail?.outlets ?? []
  const series = (detail?.series ?? []).map((point) => ({
    label: detail?.bucket === 'month' ? point.date : shortDate(point.date),
    sales: num(point.sales_ex_tax),
    invoices: point.invoice_count,
  }))

  // Back to the list carrying the shared filters, but not this page's own.
  const backSearch = new URLSearchParams(params)
  backSearch.delete('bucket')
  backSearch.delete('offset')

  return (
    <>
      <PageHeader
        title={detail?.name ?? name ?? 'Salesperson'}
        subtitle={`${formatDate(filters.from)} – ${formatDate(filters.to)}`}
      >
        <Link className="btn" to={`/sales-team?${backSearch.toString()}`}>
          ← Sales team
        </Link>
      </PageHeader>

      <div className="content stack">
        <Panel isLoading={query.isPending} error={query.error}>
          {detail && stats ? (
            <>
              {stats.sales_person_id === null ? (
                <div className="alert info">
                  These sales carry this name but no HIKE staff id, so they are counted
                  separately from any linked account with the same name.
                </div>
              ) : null}

              <div className="grid cols-4">
                <Kpi
                  label="Amount sold"
                  value={money(stats.sales_ex_tax)}
                  foot={`${percent(stats.share_pct)} of the period's sales`}
                />
                <Kpi label="Invoices" value={count(stats.invoice_count)} />
                <Kpi label="Items sold" value={count(stats.units)} />
                <Kpi label="Customers" value={count(stats.distinct_customers)} />
                <Kpi label="Average sale" value={money(stats.avg_sale)} />
                <Kpi
                  label="Items per sale"
                  value={stats.avg_basket_units === null ? '—' : stats.avg_basket_units.toFixed(1)}
                />
                <Kpi
                  label="Discounts given"
                  value={money(stats.discount_total)}
                  foot="already included in amount sold"
                />
                <Kpi
                  label="Refunds"
                  value={stats.refund_count === 0 ? '—' : money(stats.refunds_total)}
                  foot={
                    stats.refund_count === 0
                      ? 'none in this period'
                      : `${count(stats.refund_count)} refund${stats.refund_count === 1 ? '' : 's'}`
                  }
                />
                {stats.margin !== null ? (
                  <Kpi
                    label="Margin"
                    value={money(stats.margin)}
                    foot={percent(stats.margin_pct)}
                  />
                ) : null}
                <Kpi
                  label="Last sale"
                  value={formatDate(stats.last_sale)}
                  foot={`first in period ${formatDate(stats.first_sale)}`}
                />
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Sales trend</h2>
                  <div className="seg">
                    {BUCKETS.map((option) => (
                      <button
                        key={option}
                        className={bucket === option ? 'on' : ''}
                        onClick={() => setBucket(option)}
                      >
                        {option === 'auto' ? 'Auto' : option.charAt(0).toUpperCase() + option.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="card-body">
                  <Panel isLoading={false} error={null} isEmpty={series.length === 0}>
                    <div className="chart tall">
                      <ResponsiveContainer>
                        <ComposedChart data={series} margin={{ top: 6, right: 12, bottom: 0, left: 6 }}>
                          <CartesianGrid {...GRID} vertical={false} />
                          <XAxis dataKey="label" {...AXIS} minTickGap={24} />
                          <YAxis
                            yAxisId="money"
                            {...AXIS}
                            width={62}
                            tickFormatter={(value) => moneyCompact(value)}
                          />
                          <YAxis yAxisId="invoices" orientation="right" {...AXIS} width={40} />
                          <Tooltip
                            content={
                              <ChartTip
                                format={(label, value) =>
                                  label === 'Amount sold' ? money(value) : count(value)
                                }
                              />
                            }
                            cursor={{ fill: 'var(--page)' }}
                          />
                          <Bar
                            yAxisId="money"
                            dataKey="sales"
                            name="Amount sold"
                            fill="var(--brand)"
                            radius={[3, 3, 0, 0]}
                          />
                          <Line
                            yAxisId="invoices"
                            type="monotone"
                            dataKey="invoices"
                            name="Invoices"
                            stroke="var(--blue)"
                            strokeWidth={2}
                            dot={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="grid cols-2">
                <div className="card">
                  <div className="card-head">
                    <h2>What they sell</h2>
                    <span className="hint">top 10 by revenue</span>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={topProducts.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th className="num">Revenue</th>
                              <th className="num">Units</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topProducts.map((row) => (
                              <tr key={`${row.sku ?? ''}|${row.name}`}>
                                <td>
                                  {row.sku ? (
                                    <Link to={`/products/${encodeURIComponent(row.sku)}`}>
                                      {row.name}
                                    </Link>
                                  ) : (
                                    row.name
                                  )}
                                </td>
                                <td className="num strong">{money(row.revenue)}</td>
                                <td className="num">{count(row.units)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Who they sell to</h2>
                    <span className="hint">
                      top {count(topCustomers.length)} of {count(stats.distinct_customers)}
                    </span>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={topCustomers.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Customer</th>
                              <th className="num">Amount sold</th>
                              <th className="num">Invoices</th>
                              <th className="num">Last</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topCustomers.map((row) => (
                              <tr key={row.customer_id}>
                                <td>
                                  <Link to={`/customers/${row.customer_id}`}>{row.name}</Link>
                                  <div className="cell-sub">{row.phone ?? '—'}</div>
                                </td>
                                <td className="num strong">{money(row.sales_ex_tax)}</td>
                                <td className="num">{count(row.invoice_count)}</td>
                                <td className="num cell-sub">{formatDate(row.last_purchase)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
                </div>
              </div>

              <div className="card">
                  <div className="card-head">
                    <h2>Where they sell</h2>
                    <span className="hint">{count(outlets.length)} branches</span>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={outlets.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Branch</th>
                              <th className="num">Amount sold</th>
                              <th className="num">Invoices</th>
                            </tr>
                          </thead>
                          <tbody>
                            {outlets.map((row) => (
                              <tr key={row.outlet_id ?? row.outlet_name ?? 'none'}>
                                <td>{branchName(row.outlet_name)}</td>
                                <td className="num strong">{money(row.sales_ex_tax)}</td>
                                <td className="num">{count(row.invoice_count)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Invoices</h2>
                  <span className="hint">
                    {count(detail.invoices_total)} in the period
                    {stats.refund_count > 0
                      ? ` — ${count(stats.invoice_count)} sales and ${count(stats.refund_count)} refunds`
                      : ''}
                  </span>
                </div>
                <div className="card-body flush">
                  <Panel isLoading={false} error={null} isEmpty={detail.invoices.length === 0}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th className="num">Date</th>
                            <th>Customer</th>
                            <th>Branch</th>
                            <th>Status</th>
                            <th className="num">Items</th>
                            <th className="num">Amount (ex. tax)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.invoices.map((row) => (
                            <tr key={row.invoice_id}>
                              <td>
                                <Link to={`/invoices/${row.invoice_id}`}>
                                  {row.hike_invoice_number ?? row.reference}
                                </Link>
                              </td>
                              <td className="num cell-sub">{formatDate(row.date)}</td>
                              <td>{row.customer_name}</td>
                              <td className="cell-sub">{branchName(row.outlet_name)}</td>
                              <td>
                                <StatusChip status={row.status} />
                              </td>
                              <td className="num">{count(row.units)}</td>
                              <td className="num strong">{money(row.sales_ex_tax)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <Pager
                      offset={offset}
                      limit={limit}
                      total={detail.invoices_total}
                      onChange={setOffset}
                    />
                  </Panel>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
